import { computed, ref } from 'vue'
import { HttpStatusCode, type Product } from '@scayle/storefront-nuxt'
import type { Pagination } from '@scayle/storefront-api'
import type { LocationQuery } from 'vue-router'

import { type SortValue, sortingValues } from '../../utils/sort'
import { withWhitelistedQuickFilters } from '../../utils/filter'
import { DEFAULT_PRICE_PROMOTION_KEY } from '../../constants/withParameters'
import { INCLUDED_FILTERS } from '../../constants/filters'
import { DIRECT_ORDER_EXCLUDE_FILTER } from '../../constants/search'
// `~/` in a layer intentionally resolves to the consuming app's brand-specific
// util (see AGENTS.md path-resolution cheat sheet) — same pattern as
// useSearchResults.ts.
import useOdoscope from '../../composables/plp/useOdoscope'
import useRootCategories from '../../composables/useRootCategories'
import useSmartShopConfiguration from '../../composables/useSmartShopConfiguration'
import useSorting from '../../composables/useSorting'
import { shapeProductCardPayloads } from '../../utils/productPayload'
import { filterDirectOrderProducts } from '../../utils/filterDirectOrderProducts'
import { createOdoscopeRouteStateKey } from '../../utils/odoscopeStateKey'
import { isDirectOrder } from '~/utils/product'

import type { SbListingPage } from '~/cms/storyblok/types/storyblok.gen'

import useCms from '~/composables/cms/useCms'
import useProductListingPolicy from '~/composables/plp/useProductListingPolicy'
import { getPlpPath } from '~/utils/route'
import { PRODUCTS_PER_PAGE } from '~/constants/product'
import { useCurrentShop } from '#storefront/composables'
import { useRoute, useState } from '#app/composables'
import { showError, createError } from '#app/composables/error'

export default () => {
  const route = useRoute()
  const currentShop = useCurrentShop()

  const { appliedFilter } = useAppliedFilters()
  const rootCategories = useRootCategories()
  const smartShopConfiguration = useSmartShopConfiguration()
  const { selectedSort, isDefaultSort } = useSorting()

  const plpPath = computed(() => getPlpPath(route.params.slug as string))
  const routeStateKey = computed(() =>
    createOdoscopeRouteStateKey(route.fullPath),
  )
  const fetchingKey = `usePlp-${plpPath.value}`

  const cms = useCms<SbListingPage>(`useCms-${fetchingKey}`)

  const {
    availableFilters,
    unfilteredFilters,

    applyFilters,
    applyAttributeFilter,
    applyPriceFilter,
    applyBooleanFilter,
    resetFilter,
    resetFilters,
    resetPriceFilter,

    status: filterStatus,
    fetch: fetchFilters,
    refresh: refreshFilterData,
  } = useFilterNew(
    // ECOM-2639: exclude direct-order products from the PLP facet/filter universe
    // too, so available filters and counts match the excluded listing result set.
    { immediate: false, excludeDirectOrder: true },
    `useFilterNew-${fetchingKey}`,
  )

  const categoriesForListing = useCategories(
    {
      params: computed(() => ({
        path: plpPath.value,
      })),
      options: { immediate: false, watch: [] },
    },
    `useCategories-${fetchingKey}`,
  )

  const activeCategoryForListing = computed(
    () => categoriesForListing.data.value?.activeNode,
  )

  const customSorting = computed(() => {
    const customSortingKey =
      activeCategoryForListing.value?.shopLevelCustomData?.sortingKey

    if (
      isDefaultSort.value &&
      customSortingKey &&
      typeof customSortingKey === 'string'
    ) {
      return {
        sortingKey: customSortingKey,
      }
    }
  })

  // ECOM-2669: a curated category (manual `shopLevelCustomData.sortingKey` on the
  // default sort) must be served deterministically by SAPI so its merchant order
  // is identical on hard reload and client navigation. Odoscope applies its own
  // optimized order and never receives this key, so the listing policy routes
  // these categories to SAPI instead. Derived from `customSorting`, which already
  // encodes "default sort AND a string sortingKey".
  const hasCustomSort = computed(() => Boolean(customSorting.value))

  // The default `topseller` sort serves the PLP through Odoscope (which applies
  // filters itself); any other sort falls back to SAPI's `getProductsByCategory`,
  // which strips included quick filters (`sale`) from `where.attributes`. Route
  // them through `whitelistAttributes` so they survive — see the helper.
  // ECOM-2639: append the direct-order exclusion onto the same `whitelistAttributes`
  // list (concatenated, not overwritten) so the Scayle path hides them server-side
  // while pagination/counts stay correct. The Odoscope path is covered by the
  // client-side `isDirectOrder` guard on `products` below.
  const listingWhere = computed(() => {
    const where = withWhitelistedQuickFilters(
      appliedFilter.value,
      INCLUDED_FILTERS,
    )

    return {
      ...where,
      whitelistAttributes: [
        ...(where.whitelistAttributes ?? []),
        DIRECT_ORDER_EXCLUDE_FILTER,
      ],
    }
  })

  const productsForListingParams = computed(() => ({
    with: PRODUCT_LISTING_WITH,
    includedFilters: INCLUDED_FILTERS,
    pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
    includeSoldOut: false,

    category: plpPath.value,
    ...(route.query.page && { page: +route.query.page }),
    where: listingWhere.value,
    sort: {
      ...selectedSort.value,
      ...customSorting.value,
    },
    perPage: PRODUCTS_PER_PAGE,
  }))

  type ListingData = {
    products: Product[]
    pagination?: Pagination
  }
  type ListingResponse = Partial<ListingData> & {
    data?: Partial<ListingData>
  }

  const fallbackListingByRoute = useState<Record<string, ListingData>>(
    'plpFallbackListingByRoute',
    () => ({}),
  )
  const fallbackStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle')
  const fallbackError = ref<unknown>()
  const currentFallbackListing = computed(
    () => fallbackListingByRoute.value[routeStateKey.value],
  )

  const productsForListing = {
    refresh: async () => {
      const fetchRoute = routeStateKey.value
      fallbackStatus.value = 'pending'
      fallbackError.value = undefined

      try {
        const params = {
          trackSearchAnalyticsEvent: Boolean(
            productsForListingParams.value.page === 1 &&
            productsForListingParams.value.where?.term,
          ),
          ...productsForListingParams.value,
        }
        const response = await $fetch<ListingResponse>(
          '/rpc/getProductsByCategory',
          {
            method: 'GET',
            baseURL: '/api',
            headers: {
              'x-shop-id': `${currentShop.value?.shopId}`,
            },
            params: {
              payload: JSON.stringify(params),
            },
          },
        )

        if (routeStateKey.value !== fetchRoute) {
          return
        }

        const listing = response.data ?? response

        fallbackListingByRoute.value = {
          [fetchRoute]: {
            products: shapeProductCardPayloads(listing.products ?? []),
            pagination: listing.pagination,
          },
        }
        fallbackStatus.value = 'success'
      } catch (err) {
        if (routeStateKey.value !== fetchRoute) {
          return
        }

        fallbackError.value = err
        fallbackStatus.value = 'error'
        throw err
      }
    },
    error: fallbackError,
    status: fallbackStatus,
    products: computed(() => currentFallbackListing.value?.products ?? []),
    pagination: computed(() => currentFallbackListing.value?.pagination),
    totalProductsCount: computed(
      () => currentFallbackListing.value?.pagination?.total ?? 0,
    ),
    paginationOffset: computed(() => {
      const page = currentFallbackListing.value?.pagination?.page ?? 1
      const perPage =
        currentFallbackListing.value?.pagination?.perPage ??
        productsForListingParams.value.perPage ??
        0

      return (page - 1) * perPage
    }),
  }

  const odoscope = useOdoscope()
  const listingPolicy = useProductListingPolicy({ selectedSort, hasCustomSort })
  const forceFallbackRoute = ref('')
  const showOdoscopePlp = computed(
    () =>
      forceFallbackRoute.value !== routeStateKey.value &&
      listingPolicy.value.source === 'odoscope' &&
      !odoscope.error.value,
  )
  const routeProductsRefreshing = ref(false)
  const activeFetchRoute = ref('')

  // ECOM-2692: state consumed by usePlpNavigationReconcile to decide whether a
  // post-hydration self-heal is needed for the active route. Source-aware
  // (Odoscope vs the Scayle storefront-listing fallback) so reconciliation
  // covers sorted/filtered PLPs (scayle path), not only the default Odoscope one.

  // Did the active route's category fetch resolve *successfully*? A successful
  // fetch with no activeNode is a genuine 404 — terminal, so reconciliation
  // stops (the 404 surfaces). A transient 'error' status is deliberately NOT
  // terminal so a recoverable blip during the hydration window can be retried.
  const categoryResolutionAttempted = computed(
    () => categoriesForListing.status.value === 'success',
  )

  // Is this a client-execution PLP (products fetched on the client, not SSR'd)?
  // The reconcile gate — replaces the old onMounted's unconditional client fetch,
  // so it must cover every client-execution PLP regardless of Odoscope vs Scayle.
  const isClientExecution = computed(
    () => listingPolicy.value.execution === 'client',
  )

  // Has the active route's product fetch completed, on whichever source is live?
  const productsSettled = computed(() =>
    showOdoscopePlp.value
      ? odoscope.isReady.value
      : productsForListing.status.value === 'success',
  )

  // Is a product fetch currently running for the active route (any source or a
  // full fetch())? Excludes the "not yet attempted" window so the first
  // reconcile can fire; gates re-entry once a fetch is in flight.
  const isFetchInFlight = computed(
    () =>
      routeProductsRefreshing.value ||
      (showOdoscopePlp.value
        ? odoscope.productsFetching.value
        : productsForListing.status.value === 'pending'),
  )

  // For client-execution PLPs (non-crawler) products are not fetched on the
  // server — the first fetch happens client-side in onMounted. Track whether
  // that first client fetch has been attempted so the initial loading window
  // (see `productsFetching`) can render skeletons instead of the empty state.
  const hasAttemptedClientProductFetch = ref(false)

  const fetchProducts = async () => {
    const fetchRoute = routeStateKey.value

    if (import.meta.server) {
      const decision = listingPolicy.value
      if (decision.execution === 'server') {
        await productsForListing.refresh()
      }

      return
    }

    // ECOM-2669: the source decision (Odoscope vs SAPI) branches on the active
    // category's manual sort key, so the category must be resolved before we
    // read the policy. `fetch()` resolves it up front; this guard covers
    // standalone calls (e.g. filter/pagination refetches) where it may not yet
    // be present. Check `activeNode` data presence — not just fetch status —
    // mirroring the sibling-cache guard in useOdoscope.
    if (!activeCategoryForListing.value) {
      await categoriesForListing.refresh()
    }

    if (routeStateKey.value !== fetchRoute) {
      return
    }

    const decision = listingPolicy.value

    try {
      if (decision.source === 'odoscope') {
        await odoscope.fetchProducts(
          productsForListingParams.value,
          decision.tokenSlot,
          // Share the already-resolved category so Odoscope skips its own
          // (separately-keyed) category fetch instead of running it serially.
          activeCategoryForListing.value?.id,
        )
        if (routeStateKey.value !== fetchRoute) {
          return
        }
        if (!odoscope.error.value && odoscope.isReady.value) {
          forceFallbackRoute.value = ''
          return
        }
      }

      forceFallbackRoute.value = fetchRoute
      await productsForListing.refresh()
    } finally {
      // First client fetch settled (success, empty, or error) — the initial
      // loading window ends so products or the genuine empty state can render.
      if (routeStateKey.value === fetchRoute) {
        hasAttemptedClientProductFetch.value = true
      }
    }
  }

  const fetchContent = async () => {
    if (!activeCategoryForListing.value) {
      throw showError(
        createError({
          statusCode: HttpStatusCode.NOT_FOUND,
          message: 'Category not found',
        }),
      )
    }

    await cms.fetchBySlug(`categories/${activeCategoryForListing.value.id}`)
  }

  // +----------+
  // | fetching |
  // +----------+
  const fetch = async () => {
    const fetchRoute = routeStateKey.value
    // Re-entrancy guard (ECOM-2692): fetch() is triggered from the route watcher
    // and from reconcile(); without this, concurrent calls would each run
    // fetchContent/fetchFilters/smartShop (only the Odoscope products leg is
    // deduped internally), issuing duplicate getCategoriesByPath/getFilters RPCs.
    // A fetch for a newer route must still supersede the stale request.
    if (
      routeProductsRefreshing.value &&
      activeFetchRoute.value === fetchRoute
    ) {
      return
    }
    activeFetchRoute.value = fetchRoute
    routeProductsRefreshing.value = true

    try {
      // ECOM-2669: the product-source decision and the CMS slug fetch both need
      // the resolved category, but filters and smart-shop config do not. Fire the
      // category refresh once and gate only its two dependents on it, so filters
      // and smart-shop still run concurrently rather than waiting a round-trip.
      const categoryReady = categoriesForListing.refresh()

      await Promise.all([
        categoryReady.then(fetchContent),
        fetchFilters(),
        smartShopConfiguration.fetch(),
      ])

      if (routeStateKey.value !== fetchRoute) {
        return
      }

      await fetchProducts()
    } finally {
      if (activeFetchRoute.value === fetchRoute) {
        routeProductsRefreshing.value = false
      }
    }
  }

  const filterProducts = () =>
    Promise.all([fetchProducts(), refreshFilterData()])

  // ECOM-2639: client-side guard covering both fetch paths. It is the active
  // exclusion for the Odoscope path (which bypasses the SAPI `whitelistAttributes`
  // filter) and a safety net for the Scayle path. Besides dropping direct-order
  // products, it also strips direct-order siblings from the kept products so their
  // colour swatches never render on a PLP card (and offer no link into a
  // direct-order PDP) — the PDP already hides them via PdpColorPicker, this closes
  // the same gap on the PLP.
  const products = computed(() => {
    const list = showOdoscopePlp.value
      ? odoscope.products.value
      : productsForListing.products.value
    return filterDirectOrderProducts(list ?? [], isDirectOrder)
  })

  // For client-execution PLPs the first fetch happens in onMounted, so until it
  // has been attempted the listing is still loading — render skeletons, not the
  // "no products" empty state. Guarded on `products.length` so a warm cache
  // (e.g. SPA re-navigation to an already-visited PLP, where onMounted skips the
  // fetch) doesn't strand the listing on skeletons over real products.
  const awaitingInitialClientProducts = computed(
    () =>
      listingPolicy.value.execution === 'client' &&
      !hasAttemptedClientProductFetch.value &&
      products.value.length === 0,
  )

  const productsFetching = computed(
    () =>
      awaitingInitialClientProducts.value ||
      routeProductsRefreshing.value ||
      (showOdoscopePlp.value
        ? odoscope.productsFetching.value
        : productsForListing.status.value === 'pending'),
  )

  const fetching = computed(
    () =>
      cms.fetching.value ||
      filterStatus.value === 'pending' ||
      categoriesForListing.status.value === 'pending' ||
      productsFetching.value,
  )

  // +----------------+
  // | fallback state |
  // +----------------+
  const fallbackResultsCount = computed(
    () => productsForListing.pagination.value?.total,
  )
  const fallbackIsFirstPage = computed(
    () => productsForListing.pagination.value?.page === 1,
  )

  // +-------+
  // | state |
  // +-------+
  const resultsCount = computed(() =>
    showOdoscopePlp.value
      ? odoscope.resultsCount.value
      : fallbackResultsCount.value,
  )

  const pageCount = computed(() =>
    showOdoscopePlp.value
      ? odoscope.pageCount.value
      : productsForListing.pagination.value?.last,
  )

  const pagination = computed(() =>
    showOdoscopePlp.value
      ? odoscope.pagination.value
      : productsForListing.pagination.value,
  )

  const productsPerPage = computed(() =>
    showOdoscopePlp.value ? odoscope.productsPerPage : PRODUCTS_PER_PAGE,
  )

  const isFirstPage = computed(() =>
    showOdoscopePlp.value
      ? odoscope.isFirstPage.value
      : fallbackIsFirstPage.value,
  )

  const plpSortingValues: SortValue[] = Object.values(sortingValues)

  // +---------+
  // | content |
  // +---------+
  const heading = computed(() => activeCategoryForListing.value?.name)
  const categories = computed(() => categoriesForListing.data.value?.categories)
  const selectedCategory = computed(() => activeCategoryForListing.value)

  const sidebarCategories = computed(
    () => rootCategories.data.value?.categories ?? [],
  )

  const teaserImage = computed(() =>
    cms.data.value?.content && 'teaser_image' in cms.data.value.content
      ? cms.data.value.content.teaser_image
      : null,
  )

  const horizontalSideNavigationCategories = computed(
    () => categoriesForListing.data.value?.categories,
  )

  const cmsDataContent = computed(() => cms.data.value?.content)
  const listingContent = computed(() => cms.data.value?.content?.listing || [])

  const preListingContent = computed(
    () => cms.data.value?.content?.pre_listing_content ?? [],
  )

  const postListingContent = computed(
    () => cms.data.value?.content?.post_listing_content || [],
  )

  return {
    ...productsForListing,

    filterProducts,
    refreshFilterData,

    filters: availableFilters,
    unfilteredFilters,
    categories,
    selectedCategory,

    productsPerPage,
    pagination,
    products,
    resultsCount,
    pageCount,
    isFirstPage,
    productsFetching,

    fetching,
    fetch,
    fetchProducts,
    plpSortingValues,
    selectedSort,

    sidebarCategories,
    getCategory: rootCategories.getCategory,

    isActiveSalePeriod: smartShopConfiguration.isActiveSalePeriod,

    teaserImage,
    heading,
    horizontalSideNavigationCategories,
    cmsDataContent,
    preListingContent,
    postListingContent,
    listingContent,

    showOdoscopePlp,
    hasCustomSort,

    // ECOM-2692: read-only state consumed by usePlpNavigationReconcile to decide
    // whether a post-hydration self-heal is needed for the active route. All
    // computeds — no mutable handle is exposed (routeProductsRefreshing stays
    // internal; reconcile reads in-flight state via isFetchInFlight).
    isClientExecution,
    productsSettled,
    isFetchInFlight,
    categoryResolutionAttempted,

    applyAttributeFilter,
    applyPriceFilter,
    applyBooleanFilter,
    resetFilter,
    resetFilters,
    resetPriceFilter,

    applyFilters: async (filter: LocationQuery) => {
      await applyFilters(filter)
    },
  }
}
